import React from 'react';
import { noop } from '@fabric-ui/utils';
import { FormControl, InputLabel, styled, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { SeverityIcons, severityOptions } from './Severity';

interface ItemButtonProps {
  theme?: Theme;
  error?: boolean;
  size?: 'small' | 'large';
  isSeverity?: boolean;
}

const ItemButton = styled(ToggleButton)<ItemButtonProps>(({ theme, error, size, isSeverity }) => ({
  borderColor: 'var(--color-border-severity-none-secondary)',
  ...(errorColor => ({
    borderTopColor: errorColor,
    borderBottomColor: errorColor,
    '&.MuiToggleButtonGroup-grouped:first-of-type': {
      borderLeftColor: errorColor,
    },
    '&.MuiToggleButtonGroup-grouped:last-of-type': {
      borderRightColor: errorColor,
    },
  }))(error ? 'var(--color-border-status-danger-primary)' : undefined),
  padding: size === 'small' ? '5.5px 15px' : '5.5px 15px',
  color: 'var(--color-content-interactive-secondary-default)',
  backgroundColor: 'var(--color-surface-base-primary)',
  fontWeight: isSeverity ? 600 : 400,
  '&:hover': {
    backgroundColor: 'var(--color-surface-interactive-modifier-disabled)',
    color: 'var(--color-content-interactive-secondary-hover)',
    svg: {
      filter: 'unset',
    },
  },
  '&.Mui-selected': {
    backgroundColor: isSeverity ? 'transparent' : 'var(--color-surface-interactive-modifier-default)',
    color: 'var(--color-content-interactive-secondary-active)',
    svg: {
      filter: 'unset',
    },
    '&:hover': {
      backgroundColor: isSeverity ? 'transparent' : 'var(--color-surface-interactive-modifier-default)',
    },
  },
  svg: {
    filter: 'grayscale(1)',
    transition: theme.transitions.create(['filter']),
  },
  '& > span': {
    color: 'inherit',
    fontWeight: 'inherit',
    lineHeight: size === 'small' ? '18px' : '21px',
  },
  '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    marginLeft: 'inherit',
    borderLeft: 'none',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'var(--color-surface-interactive-modifier-disabled)',
    color: 'var(--color-content-interactive-secondary-hover)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--color-surface-base-secondary)',
    color: 'var(--color-content-interactive-secondary-disabled)',
    borderColor: 'var(--color-border-interactive-secondary-disabled)',
    '&.Mui-selected': {
      backgroundColor: 'var(--color-surface-base-secondary)',
      color: 'var(--color-content-interactive-secondary-disabled)',
    },
  },
}));

// Severity-specific styled button
const SeverityItemButton = styled(ItemButton)<ItemButtonProps & { severitycolor?: string }>(({ severitycolor }) => ({
  '&.Mui-selected': {
    backgroundColor: severitycolor || 'var(--color-surface-interactive-modifier-default)',
  },
}));

export interface ToggleOption {
  title: string;
  value: string;
}

export interface AvToggleProps {
  label?: string;
  value?: string | number | string[];
  options?: ToggleOption[];
  onChange?: (value: any) => void;
  error?: string;
  labelColor?: string;
  multiSelect?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  size?: 'medium' | 'small' | 'smallOutline';
  getItemColor?: (v) => string | undefined;
  getItemIcon?: (v) => string | undefined;
  isSeverity?: boolean;
}

export const AvToggle: React.FC<AvToggleProps> = ({
  label = '',
  value = '',
  onChange = noop,
  error = '',
  multiSelect = false,
  isRequired = false,
  disabled = false,
  options = [],
  size = 'medium',
  getItemColor,
  getItemIcon,
  labelColor,
  isSeverity = false,
}) => {
  const handleChange = (e, val) => {
    if (!isRequired || multiSelect || val !== null) {
      onChange(val);
    }
  };

  const ButtonComponent = isSeverity ? SeverityItemButton : ItemButton;

  return (
    <FormControl required={isRequired} error={Boolean(error)} disabled={disabled}>
      <InputLabel sx={{ '&.MuiFormLabel-root': { color: labelColor } }}>{label}</InputLabel>
      <ToggleButtonGroup value={value} exclusive={!multiSelect} onChange={handleChange} aria-label={label} disabled={disabled} size={size}>
        {options.map(({ title, value: val }) => (
          <ButtonComponent
            key={val}
            value={val}
            error={Boolean(error)}
            isSeverity={isSeverity}
            severitycolor={isSeverity && getItemColor ? getSeverityBackgroundColor(getItemColor(val)) : undefined}
            disabled={isRequired && multiSelect && Array.isArray(value) && value.length === 1 && value.includes(val)}>
            {getItemIcon?.(val)}
            <span>{title}</span>
          </ButtonComponent>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
};

const getSeverityBackgroundColor = (severity: string | undefined) => {
  const colorMap = {
    error: 'var(--color-surface-severity-critical-primary)',
    high: 'var(--color-surface-severity-high-primary)',
    warning: 'var(--color-surface-severity-medium-primary)',
    low: 'var(--color-surface-severity-low-primary)',
    info: 'var(--color-surface-severity-none-primary)',
    unknown: 'var(--color-surface-severity-none-primary)',
  };
  return severity ? colorMap[severity] : undefined;
};

interface SeverityToggleProps {
  onChange: (value: string) => void;
}
export function SeverityToggle({ onChange, ...toggleProps }: SeverityToggleProps & any) {
  return (
    <AvToggle
      options={severityOptions}
      getItemColor={val => SeverityIcons[val].color}
      getItemIcon={val => SeverityIcons[val].icon}
      onChange={newValue =>
        onChange(toggleProps.multiSelect ? newValue.sort((a, b) => SeverityIcons[a].order - SeverityIcons[b].order) : newValue)
      }
      isSeverity={true}
      {...toggleProps}
    />
  );
}
